import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { apiLogin, getMe } from "@/lib/api";
import { toast } from "sonner";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// Minutos de inactividad antes del cierre de sesión automático (configurable).
const INACTIVITY_MINUTES = Number(process.env.REACT_APP_INACTIVITY_MINUTES) || 15;
const INACTIVITY_MS = INACTIVITY_MINUTES * 60 * 1000;
const ACTIVITY_EVENTS = ["mousedown", "mousemove", "keydown", "scroll", "touchstart", "click"];

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const lastActivityRef = useRef(Date.now());
  const idleTimerRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      setLoading(false);
      return;
    }
    getMe()
      .then(setUser)
      .catch(() => localStorage.removeItem("token"))
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    const data = await apiLogin({ email, password });
    localStorage.setItem("token", data.access_token);
    lastActivityRef.current = Date.now();
    setUser(data.user);
    return data.user;
  };

  const logout = (reason) => {
    localStorage.removeItem("token");
    setUser(null);
    if (reason) toast.warning(reason);
  };

  // Cierre de sesión por inactividad (solo con sesión activa).
  useEffect(() => {
    if (!user) return undefined;

    const markActivity = () => { lastActivityRef.current = Date.now(); };
    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, markActivity, { passive: true }));

    idleTimerRef.current = setInterval(() => {
      if (Date.now() - lastActivityRef.current >= INACTIVITY_MS) {
        logout(`Sesión cerrada por inactividad (${INACTIVITY_MINUTES} min).`);
      }
    }, 15000); // chequeo cada 15s

    return () => {
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, markActivity));
      if (idleTimerRef.current) clearInterval(idleTimerRef.current);
    };
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, inactivityMinutes: INACTIVITY_MINUTES }}>
      {children}
    </AuthContext.Provider>
  );
}
